import React from 'react';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';

import { ScrollReveal } from '../../ScrollReveal';
import RichText from '../../RichText';

import { TableProps } from '../shared';

export function StandardTable({
  tableId,
  title,
  tableBody,
  tableFootnotes
}: TableProps) {
  const RICH_TEXT_OVERRIDES: RenderNode = {
    [BLOCKS.TABLE]: (_node, children) => (
      <table className="w-full border-collapse">
        <tbody>{children}</tbody>
      </table>
    ),
    [BLOCKS.TABLE_ROW]: (_node, children) => (
      <tr className="text-left [&:nth-of-type(2n+3)]:bg-lighter-gray">
        {children}
      </tr>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => (
      <th className="p-2 text-left" data-testid="st-header-cell">
        {children}
      </th>
    ),
    [BLOCKS.TABLE_CELL]: (_node, children) => (
      <td className="p-2 [&_p]:!pt-0">{children}</td>
    ),
    [BLOCKS.PARAGRAPH]: (_node, children) => (
      <p className="[&:not(:first-of-type)]:pt-4">{children}</p>
    )
  };

  return (
    <ScrollReveal>
      <div
        className="block-container mb-12 mt-12 tablet:mb-16 tablet:mt-16"
        id={tableId!}
        data-testid="standard-table"
      >
        <div className="-mx-2 box-border flex w-full flex-1 flex-wrap justify-center tablet:-mx-3 desktop:-mx-4">
          <div className="flex w-full shrink-0 grow-0 flex-col px-2 tablet:w-2/3 tablet:px-3 desktop:px-4">
            {title && <p className="mb-8 text-center font-bold">{title}</p>}

            <RichText
              data={tableBody}
              overrideOptions={RICH_TEXT_OVERRIDES}
              overrideMarkOptions={{
                [MARKS.CODE]: (children) => (
                  <a
                    className="text-navy no-underline"
                    id={`${tableId}-footnote-ref-${children}`.trim()}
                    href={`#${tableId}-footnote-${children}`.trim()}
                    aria-describedby={`${tableId}-footnotes`}
                    role="doc-noteref"
                  >
                    <sup>{children}</sup>
                  </a>
                )
              }}
            />

            {tableFootnotes && (
              <div id={`${tableId}-footnotes`}>
                <RichText
                  data={tableFootnotes}
                  overrideOptions={RICH_TEXT_OVERRIDES}
                  overrideMarkOptions={{
                    [MARKS.CODE]: (children) => (
                      <a
                        className="text-navy no-underline"
                        id={`${tableId}-footnote-${children}`.trim()}
                        href={`#${tableId}-footnote-ref-${children}`.trim()}
                        aria-label={`Back to reference ${children}`}
                        role="doc-backlink"
                      >
                        <sup>{children}</sup>
                      </a>
                    )
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
}
